import React, { useState } from 'react';
import { FiBook, FiPlay, FiFileText, FiHelpCircle, FiExternalLink, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import Header from '../components/layout/Header';
import Card from '../components/ui/Card';
import toast from 'react-hot-toast';
import { CHARACTERS } from '../assets/characters';

const GuidesPage: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const guides = [
    {
      icon: <FiPlay size={20} />,
      titre: 'Prise en main de JULIA',
      description: 'Découvrez les fonctionnalités principales de la plateforme et configurez votre espace de travail.',
      type: 'Vidéo',
      color: 'var(--color-primary)',
    },
    {
      icon: <FiBook size={20} />,
      titre: 'Paramétrer Ma structure',
      description: 'Comment renseigner les informations de votre entreprise pour que JULIA soit opérationnelle.',
      type: 'Guide PDF',
      color: '#0284C7',
    },
    {
      icon: <FiBook size={20} />,
      titre: 'Gérer le registre du personnel',
      description: 'Comprendre les obligations légales et comment tenir le registre unique du personnel.',
      type: 'Guide PDF',
      color: 'var(--color-secondary)',
    },
    {
      icon: <FiPlay size={20} />,
      titre: 'Lancer un audit RH',
      description: 'Comment interpréter les scores d\'audit et mettre en place un plan d\'action.',
      type: 'Vidéo',
      color: 'var(--color-success)',
    },
    {
      icon: <FiFileText size={20} />,
      titre: 'Générer des documents RH',
      description: 'Contrats, avenants, lettres : comment utiliser l\'IA pour générer des documents adaptés.',
      type: 'Guide PDF',
      color: 'var(--color-warning)',
    },
  ];

  const faqs = [
    {
      question: 'Comment fonctionne l\'IA de JULIA ?',
      reponse: 'JULIA utilise Google Gemini, un modèle d\'IA avancé, pour analyser vos données RH et vous apporter des réponses adaptées au droit du travail français. Toutes les réponses incluent une mention rappelant qu\'elles ne constituent pas des conseils juridiques professionnels.',
    },
    {
      question: 'Mes données sont-elles sécurisées ?',
      reponse: 'Oui. Chaque instance JULIA est isolée et dédiée à votre entreprise. Vos données ne sont jamais partagées avec d\'autres clients. La plateforme est conforme au RGPD.',
    },
    {
      question: 'Comment mettre à jour mes obligations selon mon effectif ?',
      reponse: 'Mettez à jour le nombre de salariés dans le module "Ma structure > Mes informations". Les obligations employeur se mettent automatiquement à jour en fonction de votre effectif.',
    },
    {
      question: 'Puis-je exporter mes données ?',
      reponse: 'Oui, depuis le module "Gérer mes salariés > Registre unique", vous pouvez exporter la liste de vos salariés en format Excel ou PDF.',
    },
    {
      question: 'Comment contacter le support ?',
      reponse: 'Pour toute question, utilisez le chat JULIA (icône en bas à droite) ou contactez votre référent JULIA par email.',
    },
  ];

  return (
    <div>
      <Header title="Guides utilisateurs" subtitle="Supports PDF, vidéos et FAQ pour maîtriser JULIA" />
      <div style={{ padding: '0 2rem' }}>
        <div style={{
          marginTop: '1rem',
          marginBottom: '0.25rem',
          background: 'linear-gradient(135deg, #ECFEFF 0%, #E0F2FE 100%)',
          borderRadius: '1rem',
          padding: '1.25rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
        }}>
          <div>
            <p style={{ margin: 0, fontWeight: 700, color: 'var(--color-primary)' }}>Progression RH</p>
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.9rem', color: 'var(--color-text-secondary)', maxWidth: 420 }}>
              Guides, vidéos et FAQ pour monter en compétences au fil de votre usage de JULIA.
            </p>
          </div>
          <img src={CHARACTERS.guides} alt="" aria-hidden style={{ width: 100, height: 'auto' }} />
        </div>
      </div>


      <div style={{ padding: '1.5rem 2rem' }}>
        {/* Guides */}
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600, marginBottom: '1rem', marginTop: 0 }}>
          Supports de formation
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {guides.map((g, i) => (
            <Card key={i} style={{ cursor: 'pointer' }} onClick={() => toast.success(`"${g.titre}" sera disponible prochainement`)}>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', background: g.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: g.color }}>{g.icon}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{g.titre}</span>
                    <span style={{ fontSize: '0.65rem', padding: '0.125rem 0.5rem', borderRadius: 'var(--radius-full)', background: g.color + '20', color: g.color, fontWeight: 500 }}>{g.type}</span>
                  </div>
                  <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 0 }}>{g.description}</p>
                </div>
                <FiExternalLink size={14} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
              </div>
            </Card>
          ))}
        </div>

        {/* Prochainement */}
        <div style={{ padding: '1rem', background: 'var(--color-primary)08', border: '1px dashed var(--color-primary)40', borderRadius: 'var(--radius-md)', marginBottom: '2rem', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
          Des supports supplémentaires seront ajoutés prochainement : lecture d'un bulletin de salaire, règles d'archivage, droits à congés, gestion de la maladie (carence, calcul des droits).
        </div>

        {/* FAQ */}
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600, marginBottom: '1rem', marginTop: 0 }}>
          Questions fréquentes
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {faqs.map((faq, i) => (
            <Card key={i} style={{ cursor: 'pointer' }} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <FiHelpCircle size={18} style={{ color: 'var(--color-primary)', flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', margin: 0 }}>{faq.question}</p>
                    {openFaq === i ? <FiChevronUp size={14} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} /> : <FiChevronDown size={14} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />}
                  </div>
                  {openFaq === i && (
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: '0.5rem 0 0' }}>{faq.reponse}</p>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GuidesPage;
